import { useState } from "react";
import LedPicker, { type LedPickerValue } from "./LedPicker";
import {
  useCreatePreset,
  useDeletePreset,
  usePresets,
  useUpdatePreset,
  useOverride,
} from "../api/queries";
import type { Preset } from "../api/types";
import { ApiError } from "../api/client";

const TEST_TTL_SECONDS = 5 * 60;

export default function PresetEditor() {
  const presets = usePresets();
  const create = useCreatePreset();
  const update = useUpdatePreset();
  const del = useDeletePreset();
  const testOverride = useOverride();

  const [selectedId, setSelectedId] = useState<number | "new">("new");
  const [name, setName] = useState("");
  const [value, setValue] = useState<LedPickerValue>({ color: "#FFAA00", luminance: 70, effect: "solid" });
  const [error, setError] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  function selectPreset(p: Preset | null) {
    setError(null);
    if (!p) {
      setSelectedId("new");
      setName("");
      setValue({ color: "#FFAA00", luminance: 70, effect: "solid" });
      return;
    }
    setSelectedId(p.id);
    setName(p.name);
    setValue({ color: p.color, luminance: p.luminance, effect: p.effect });
  }

  async function save() {
    setError(null);
    try {
      if (selectedId === "new") {
        const res = await create.mutateAsync({
          name, color: value.color, luminance: value.luminance, effect: value.effect,
        });
        selectPreset(res.preset);
      } else {
        const res = await update.mutateAsync({
          id: selectedId as number, name, color: value.color,
          luminance: value.luminance, effect: value.effect,
        });
        selectPreset(res.preset);
      }
    } catch (err) {
      setError(err instanceof ApiError ? String(err.detail) : "save failed");
    }
  }

  async function remove() {
    setError(null);
    if (selectedId === "new") return;
    try {
      await del.mutateAsync(selectedId as number);
      selectPreset(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("This preset is referenced by one or more rules.");
      } else {
        setError(err instanceof ApiError ? String(err.detail) : "delete failed");
      }
    }
  }

  async function testCurrent() {
    setError(null);
    setTestMsg(null);
    try {
      await testOverride.mutateAsync({
        color: value.color,
        luminance: value.luminance,
        effect: value.effect,
        ttl_seconds: TEST_TTL_SECONDS,
      });
      setTestMsg(`Applied to LEDs for ${TEST_TTL_SECONDS / 60} min. Clear the override from the status page to end early.`);
    } catch (err) {
      setError(err instanceof ApiError ? String(err.detail) : "test failed");
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
      <aside className="space-y-2 rounded-xl border border-slate-800 bg-slate-900 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Presets</div>
        <ul className="space-y-1">
          {(presets.data?.presets ?? []).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => selectPreset(p)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                  selectedId === p.id ? "bg-slate-800" : "hover:bg-slate-800/60"
                }`}
              >
                <span
                  className="inline-block h-4 w-4 rounded border border-slate-700"
                  style={{ backgroundColor: p.effect === "off" ? "#000" : p.color, opacity: p.effect === "off" ? 0.3 : p.luminance / 100 }}
                />
                <span>{p.name}</span>
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => selectPreset(null)}
          className="mt-2 w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs hover:border-slate-500"
        >
          + New preset
        </button>
      </aside>

      <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-4">
        <label className="block">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Name</div>
          <input
            className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="preset-name"
          />
        </label>
        <LedPicker value={value} onChange={setValue} />
        {error ? <div className="text-sm text-rose-400">{error}</div> : null}
        {testMsg ? <div className="text-sm text-emerald-300">{testMsg}</div> : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={save}
            disabled={!name}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-slate-950 hover:bg-amber-400 disabled:opacity-50"
          >
            {selectedId === "new" ? "Create preset" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={testCurrent}
            disabled={testOverride.isPending}
            title={`Apply the current color to the LEDs for ${TEST_TTL_SECONDS / 60} min`}
            className="rounded-md border border-slate-700 bg-slate-800 px-4 py-2 text-sm hover:border-slate-500 disabled:opacity-50"
          >
            {testOverride.isPending ? "Testing…" : `Test on LEDs (${TEST_TTL_SECONDS / 60} min)`}
          </button>
          {selectedId !== "new" ? (
            <button
              type="button"
              onClick={remove}
              className="rounded-md border border-rose-700 bg-rose-900/40 px-4 py-2 text-sm text-rose-200 hover:bg-rose-900/60"
            >
              Delete
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
